import React, { Fragment } from 'react'
import Img from "gatsby-image"

import { MdPlace, MdNetworkCheck, MdLabel, MdInfoOutline, MdVideoLibrary } from "react-icons/md"
import { IoMdArrowDropright, IoMdMegaphone } from "react-icons/io"
import { GiTwoCoins } from "react-icons/gi"
import { GoVerified } from "react-icons/go"

import { css } from '@emotion/core'
import { constants } from '../styles/colors'
import { lighten } from 'polished'

import { cleanLink } from '../helpers/helpers'
import { i18nStrings } from '../helpers/translations'

const ListItem = (props) => {

    const {
        index,
        lang,
        sponsored,
        brand,
        date,
        date_span,
        title,
        desc,
        link,
        link_title,
        follow,
        tags,
        stats,
        place,
        price,
        image,
        img_source,
        curators,
        curators_notes,
        videos,
    } = props

    return (
        <Fragment>
            <div css={sponsored ? [listItem, sponsoredItem] : listItem}>
                {sponsored && <div css={sponsoredNotice}><IoMdMegaphone /> {i18nStrings.sponsored[lang]}</div>}
                <div css={itemContent}>
                    <div css={left}>
                        <div css={numbering}>
                            {index+1}
                        </div> 
                        <div css={main}>
                            <div>
                                {brand && brand.logo &&
                                    <div css={itemBrand}>
                                        <Img fixed={brand.logo.childImageSharp.fixed} />
                                        <span>{brand.name} {brand.verified && <GoVerified />}</span>
                                    </div>}
                                <h2>
                                    {date && <span css={itemDate}>{date}</span>}
                                    {date_span && <span css={itemDate}>{date_span.length > 1 ? date_span.join(' - ') : date_span.concat(' - ...') }</span>}
                                    {title}  
                                </h2>
                                {desc && <p>{desc}</p>}
                                {link && !sponsored &&
                                    <a  href={link} 
                                        rel={`${!follow && 'nofollow'} noreferrer noopener`} 
                                        title={link_title || cleanLink(link)}
                                        target="blank"
                                    >
                                        {i18nStrings.link[lang]} {link_title || cleanLink(link)} <IoMdArrowDropright />
                                    </a>
                                }
                                {link && sponsored &&
                                    <span css={sponsoredLink}>
                                        {i18nStrings.link[lang]} {link_title || cleanLink(link)} <IoMdArrowDropright />
                                    </span>
                                }
                            </div>                          
                            {(stats || place || tags || videos || price) && 
                                <div css={metadata}>
                                    {tags && tags.map( (tag, index) => <span key={tag} className="meta__tag">{index === 0 && <MdLabel />} {tag}</span> )}
                                    {stats && stats.map( (stat, index) => <span key={stat[0]} className="meta__stat">{index === 0 && <MdNetworkCheck />} {stat[0] + ': ' + stat[1]}</span> )}
                                    {place && <span className="meta__place"><MdPlace /> {place}</span>}
                                    {videos && videos.map( (video, index) => <span className="meta__video" key={video[0]}>{index === 0 && <MdVideoLibrary />} <a href={video[1]} target="blank" rel="nofollow noopener noreferrer">{video[0]}</a></span>)}
                                    {price && <span className="meta__price"><GiTwoCoins /> {i18nStrings.price[lang]} {price.euro && `${price.euro}€`} {price.dollar && `${price.dollar}$`}</span>}
                                </div>
                            }
                        </div>
                    </div>
                    {image &&
                        <div css={itemMedia}>
                            <Img fixed={image} />
                            {img_source && <div css={mediaSource}><span>{cleanLink(img_source)}</span> <MdInfoOutline /></div>}
                        </div>
                    }
                </div>
            </div>
            {curators && 
                <div css={itemCuratorsNotes}>
                    {curators.map( (curator, index) => (
                        <div key={curator.id}>
                            <Img fixed={curator.avatar.childImageSharp.fixed} />
                            <div className="curator__note">
                                <h5>{curator.name}</h5>
                                <span role="img" aria-label="look-up">👆</span>
                                <span>{curators_notes[index]}</span>
                            </div>
                        </div>
                    ))}
                </div> 
            }
        </Fragment>
    )
}

export default ListItem

const listItem = css`
  padding: 20px;
  background-color: ${constants.white};
  border-radius: ${constants.radiusBig};
  margin-bottom: 10px;
  box-shadow: ${constants.shadowBig};
  position: relative;
`

const sponsoredItem = css`
    border: 2px solid ${constants.accent2};
`

const sponsoredNotice = css`
    position: absolute;
    top: 0;
    left: 0px;
    font-size: 10px;
    background-color: ${lighten(0.3, constants.accent2)};
    color: ${constants.white};
    text-transform: uppercase;
    border-radius: 13px 0 ${constants.radiusSmall} 0;
    padding: 0px 8px;
    line-height: 1.7;

    svg {
        margin-bottom: -4px;
        font-size: 14px;
    }
`

const itemContent = css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media screen and (max-width: ${constants.breakpoint1}) {
        flex-direction: column; 
        align-items: start;
    }
`

const left = css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`

const numbering = css`
    min-width: 46px;
    margin-right: 20px;
    text-align: center;
    padding: 10px 0;
    border-radius: ${constants.radiusMax};
    background-color: ${constants.accent2};
    color: ${constants.white};
    font-weight: 700;
    font-size: 12px;
`

const main = css`
    margin: 0;
    line-height: 1.2;
    margin-right: 20px;

    h2 {
        display: inline;
        margin: 0 5px 0 0;
        font-weight: 500;
        font-size: 15px;
        color: ${constants.darkgrey};
    }

    p {
        display: inline;
        font-size: 15px;
        color: ${constants.midgrey};
        margin-right: 5px;
    }

    a {
        text-decoration: none;
        display: inline-block;
        color: ${constants.accent};
        font-size: 12px;
        padding: 2px 8px;
        background-color: ${constants.whitegrey};
        border-radius: ${constants.radiusSmall};
        color: ${constants.midgrey};

        svg {
            margin-bottom: -2px;
        }
    }
`

const sponsoredLink = css`
    background-color: ${constants.accent};
    border-radius: ${constants.radiusSmall};
    color: ${constants.white};
    padding: 4px 8px;
    font-size: 12px;
    text-decoration: none;
    display: inline-block;
    
    svg {
        margin-bottom: -2px;
    }
`

const itemMedia = css`
    margin-right: 0;
    margin-bottom: -5px;
    position: relative;

    img {
        border-radius: ${constants.radiusSmall};
        line-height: 0;
        display: block;
        vertical-align: bottom;
        margin-bottom: 0;
    }

    @media screen and (max-width: ${constants.breakpoint1}) {
        margin-left: 65px;
        margin-top: 10px;
    }
`

const mediaSource = css`
    position: absolute;
    top: 0;
    right: 10px;
    color: ${constants.white};
    font-size: 14px;
    animate: opacity 2s ease-in-out;

    svg {
        margin-bottom: -3px;
    }

    span {
        opacity: 0;
        font-size: 10px;
        padding: 1px 7px;
        background: rgba(0, 0, 0, .66);
        border-radius: ${constants.radiusSmall};
    }

    :hover {
        span {
            opacity: 1;
        }
    }
`

const itemBrand = css`
    display: flex;
    justify-content: flex-start;
    align-items: center;

    img {
        border-radius: ${constants.radiusMax};
        border: 1px solid ${constants.whitegrey};
    }

    span {
        font-size: 13px;
        margin-left: 10px;
        color: ${constants.midgrey};
        font-weight: 600;

        svg {
            margin-bottom: -3px;
            margin-left: 4px;
            color: ${constants.accent};
        }
    }
`

const metadata = css`
    margin-top: 15px;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    span.meta__stat, 
    span.meta__place, 
    span.meta__price,
    span.meta__video,
    span.meta__tag {
        font-size: 12px;
        margin-right: 5px;
        display: inline-block;
        padding: 5px 12px;
        margin-bottom: 5px;
        border-radius: ${constants.radiusMax};
        color: ${constants.darkgrey};
    }

    svg {
        margin-bottom: -2px;
        margin-right: 5px;
    }

    span.meta__tag {
        background-color: ${lighten(.38, constants.yellow)};

        svg {
            color: ${constants.yellow};
        }
    }

    span.meta__stat {
        background-color: ${lighten(.3, constants.green)};

        svg {
            color: ${constants.green};
        }
    }

    span.meta__place {
        background-color: ${lighten(.41, constants.blue)};

        svg {
            color: ${constants.blue};
        }
    }

    span.meta__price {
        background-color: ${lighten(.43, constants.purple)};

        svg {
            color: ${constants.purple};
        }
    }

    span.meta__video {
        background-color: ${lighten(.43, constants.red)};

        a {
            background: transparent;
            padding: 0;
            color: ${constants.darkgrey};
        }

        svg {
            color: ${constants.red};
        }
    }
`


const itemDate = css`
    font-weight: 700;
    margin: 0 10px 0 0;
    padding: 2px 8px;
    font-size: 14px;
    letter-spacing: 1px;
    background-color: ${lighten(.3, constants.accent)};
    color: ${constants.accent};
    border-radius: ${constants.radiusSmall};
    display: inline-block;
    // position: relative;
    // bottom: 0px;
`

const itemCuratorsNotes = css`
    margin: 10px 10px 20px 50px;

    > div {
        display: flex;
        align-items: flex-end;
        margin-bottom: 5px;

        img {
            border-radius: ${constants.radiusMax};
            border: 1px solid ${constants.white};
        }
    
        div.curator__note {
            padding: 5px 10px 10px 10px;
            margin-left: 5px;
            max-width: 500px;
            background-color: ${constants.accent2};
            color: ${constants.white};
            border-radius: ${constants.radiusBig} ${constants.radiusBig} ${constants.radiusBig} ${constants.radiusSmall};
            line-height: 1;

            h5 {
                display: inline;
                font-size: 12px;
                font-weight: 600;
                margin: 0 5px 0 0;
            }

            span {
                display: inline;
                font-size: 12px;
            }
        }
    }
`